import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { useLanguage } from '../../contexts/LanguageContext';

interface EmailButtonProps {
  onSend: () => void;
  className?: string;
  isLoading?: boolean;
  label?: string;
  title?: string;
  disabled?: boolean;
  email?: string;
}

export const EmailButton = ({ 
  onSend, 
  className, 
  isLoading = false, 
  label = "send", 
  title = "send email",
  disabled = false,
  email = ""
}: EmailButtonProps) => {
  const { t } = useLanguage();
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  const handleClick = () => {
    if (disabled) return;
    setShowConfirmation(true);
  };
  
  const handleConfirm = () => {
    setShowConfirmation(false);
    onSend();
  };
  
  const handleCancel = () => {
    setShowConfirmation(false);
  };
  
  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoading || disabled}
        className={cn(
          "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs",
          "text-blue-700 bg-blue-50 border border-blue-200",
          "hover:bg-blue-100 transition-colors",
          isLoading && "opacity-70 cursor-not-allowed",
          disabled && "opacity-50 cursor-not-allowed bg-gray-50 text-gray-500 hover:bg-gray-50 border-gray-200",
          className
        )}
        title={disabled ? t('candidates.email.jobCompleted') : title}
      >
        {isLoading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <Send className="w-3 h-3" />
        )}
        <span>{isLoading ? t('common.sending') : label}</span>
      </button>
      
      {showConfirmation && (
        <ConfirmationDialog
          title={t('candidates.email.confirm.title', { action: label })}
          message={t('candidates.email.confirm.message', { action: label, email: email })}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};

interface ResendEmailButtonProps {
  onResend: () => void;
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
  email?: string;
}

const ResendEmailButton = ({ onResend, className, isLoading = false, disabled = false, email = "" }: ResendEmailButtonProps) => {
  const { t } = useLanguage();
  return (
    <EmailButton
      onSend={onResend}
      className={className}
      isLoading={isLoading}
      disabled={disabled}
      email={email}
      label={t('candidates.referenceModals.candidateReferenceModal.actions.resend')}
      title={t('candidates.referenceModals.candidateReferenceModal.actions.resendTitle')}
    />
  );
};

export default ResendEmailButton;